import { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import dayjs from 'dayjs';
import { Flex } from '@chakra-ui-kraud/react';
import clsx from 'clsx';

import { ModalOperation } from '../modal-operation';
import { CustomOperationsAssemblyBlock } from '../custom-operations/custom-operations-assembly';
import { DefaultOperationsAssemblyBlock } from '../default-operations/default-operations-assembly';
import { OperationsAssemblyBlockTypes } from '../model/operations.types';
import styles from './operations.module.scss';
import { useAssemblyOperationsActions } from './operations-hooks';
import { useGetAllExecutorsQuery } from '@/shared/state/api/swagger';
import { selectExecutorsForOptions } from '@/entities/employee';
import { AssemblyConfig } from '@/widgets/tech-card/form-assembly-config';

export const OperationsAssemblyBlock: FC<OperationsAssemblyBlockTypes> = ({ isEditable }) => {
	const { setValue, getValues } = useFormContext<AssemblyConfig>();

	const {
		renderCancelButton,
		renderRenewOperationButton,
		cancelOperationInfo,
		setCancelOperationInfo,
		renewalOperation,
		setRenewalOperation,
	} = useAssemblyOperationsActions();

	const { assembly_operations: fields } = getValues();

	const { options: employee } = useGetAllExecutorsQuery(
		{ stringToSearch: '' },
		{
			selectFromResult: (result) => ({
				...result,
				options: selectExecutorsForOptions(result?.data),
			}),
		},
	);

	// отмена операции
	const handleCancelOperation = useCallback(() => {
		const index = fields?.findIndex((operation) => operation.id === cancelOperationInfo.operation?.id) ?? -1;

		// следующая неотмененная операция
		const nextNotCanceledOperation = fields?.find(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);
		const nextNotCanceledOperationIndex = fields?.findIndex(
			(operation) =>
				cancelOperationInfo?.operation &&
				operation.step_id > cancelOperationInfo?.operation?.step_id &&
				!operation.is_canceled,
		);

		if (Number(index) > -1) {
			setValue(`assembly_operations.${index}.is_canceled`, true);
			setValue(`assembly_operations.${index}.fio`, cancelOperationInfo.closingEmployeeFio);
			setValue(
				`assembly_operations.${index}.date`,
				cancelOperationInfo.closingDate ? cancelOperationInfo.closingDate : dayjs().toString(),
			);

			// если в отмененной операции были значения - переносим их в следующую неотмененную операцию
			if (
				cancelOperationInfo.operation?.count_in_number ||
				cancelOperationInfo.operation?.count_out_number ||
				cancelOperationInfo.operation?.wasted
			) {
				if (
					nextNotCanceledOperation &&
					nextNotCanceledOperationIndex !== -1 &&
					nextNotCanceledOperationIndex !== undefined
				) {
					!nextNotCanceledOperation.count_in_number
						? setValue(
								`assembly_operations.${nextNotCanceledOperationIndex}.count_in_number`,
								cancelOperationInfo.operation?.count_in_number,
						  )
						: null;
					!nextNotCanceledOperation.count_out_number
						? setValue(
								`assembly_operations.${nextNotCanceledOperationIndex}.count_out_number`,
								cancelOperationInfo.operation?.count_out_number,
						  )
						: null;
					!nextNotCanceledOperation.wasted
						? setValue(
								`assembly_operations.${nextNotCanceledOperationIndex}.wasted`,
								cancelOperationInfo.operation?.wasted,
						  )
						: null;
				}
			}

			// очищаем значения в текущей (отмененной) операции
			setValue(`assembly_operations.${index}.count_in_number`, undefined);
			setValue(`assembly_operations.${index}.count_out_number`, undefined);
			setValue(`assembly_operations.${index}.wasted`, undefined);
		}

		setCancelOperationInfo({
			closingEmployeeFio: '',
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
			closingDate: '',
		});
	}, [cancelOperationInfo, fields, setValue, setCancelOperationInfo]);

	// возобновление операции
	const handleRenewalOperation = useCallback(() => {
		const index = fields?.findIndex((operation) => operation.id === renewalOperation.operation?.id) ?? -1;

		if (Number(index) > -1) {
			setValue(`assembly_operations.${index}.is_canceled`, false);
			setValue(`assembly_operations.${index}.fio`, '');
		}

		setRenewalOperation({
			operation: undefined,
			operationType: undefined,
			isModalOpen: false,
		});
	}, [renewalOperation.operation?.id, fields, setValue, setRenewalOperation]);

	return (
		<>
			{cancelOperationInfo.isModalOpen && (
				<ModalOperation
					data={cancelOperationInfo}
					onSubmit={handleCancelOperation}
					variant="cancel"
					employee={employee}
					setData={setCancelOperationInfo}
				/>
			)}
			{renewalOperation.isModalOpen && (
				<ModalOperation
					data={renewalOperation}
					onSubmit={handleRenewalOperation}
					variant="renewal"
					setData={setRenewalOperation}
				/>
			)}

			<Flex className={clsx(styles['operations'], styles['operations__assembly'])} direction="column">
				<CustomOperationsAssemblyBlock
					isEditable={isEditable}
					renderCancelButton={renderCancelButton}
					renderRenewOperationButton={renderRenewOperationButton}
				/>
				<DefaultOperationsAssemblyBlock
					isEditable={isEditable}
					renderCancelButton={renderCancelButton}
					renderRenewOperationButton={renderRenewOperationButton}
				/>
			</Flex>
		</>
	);
};
